import React, { useMemo, useState } from 'react';

import { Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import ListPageHeader from '@shared/ListPageHeader';
import TableIndex from '@shared/table/TableIndex';
import type { SupportRule } from '@shared/table/TableIndex';
import { MultipleValue } from '@shared/table/Rules/component';
import { formatDateTime } from '@utils/dateTime';

import { replenishmentService } from '@services/operator/replenishmentService';
import type { ReplenishmentOrder } from '@services/operator/replenishmentService';

// 補貨單狀態色彩對照（值對齊 ReplenishmentOrder model enum:
//   pending / in_progress / done / canceled / closed）。
const STATUS_COLORS: Record<string, string> = {
  pending: 'default',
  in_progress: 'processing',
  done: 'success',
  canceled: 'error',
  closed: 'warning',
};

const STATUS_VALUES = ['pending', 'in_progress', 'done', 'canceled', 'closed'];

function ReplenishmentOrdersList() {
  const { t } = useTranslation();
  const [lastModified, setLastModified] = useState(0);

  const columns: unknown[] = useMemo(() => [
    {
      name: t('operator.replenishment_orders.columns.status'),
      cell: (row: ReplenishmentOrder) => (
        <Tag color={STATUS_COLORS[row.status] || 'default'}>
          {t(`enums.replenishment_order_status.${row.status}`)}
        </Tag>
      ),
    },
    {
      name: t('operator.replenishment_orders.columns.name'),
      selector: (row: ReplenishmentOrder) => row.name,
      // name 用 <Link> 而非 onClick：保留 Cmd/中鍵點擊開新分頁的瀏覽器原生行為。
      cell: (row: ReplenishmentOrder) => (
        <Link to={`/replenishment_orders/${row.id}`}>
          <div>{row.name}</div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {row.barcode}
          </Typography.Text>
        </Link>
      ),
    },
    {
      name: t('operator.replenishment_orders.columns.merchant'),
      selector: (row: ReplenishmentOrder) => row.merchant_name || '-',
    },
    {
      name: t('operator.replenishment_orders.columns.created_at'),
      selector: (row: ReplenishmentOrder) => formatDateTime(row.created_at),
    },
  ], [t]);

  const supportRules = useMemo<SupportRule[]>(() => [
    {
      label: t('operator.replenishment_orders.filters.status'),
      component: MultipleValue,
      inputName: 'status',
      defaultOptions: STATUS_VALUES.map((s) => ({
        label: t(`enums.replenishment_order_status.${s}`),
        value: s,
      })),
      createable: false,
    },
  ], [t]);

  return (
    <div>
      {/* 補貨單由補貨清單頁 (needs → preview → generate) 產生，列表頁不提供「新增」按鈕。 */}
      <ListPageHeader items={['replenishment', 'replenishment.orders']} />
      <TableIndex
        columns={columns}
        tableId="ReplenishmentOrdersTable"
        supportRules={supportRules}
        defaultQuery={{ sorting_field: 'created_at', sorting_direction: 'desc' }}
        service={replenishmentService as unknown as React.ComponentProps<typeof TableIndex>['service']}
        lastModified={lastModified}
        setLastModified={setLastModified}
      />
    </div>
  );
}

export default ReplenishmentOrdersList;
